import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Input, Checkbox, Row, Col, Space, Tag } from 'antd';
import { debounce } from 'lodash';
import { ResponsiveWaffle } from "@nivo/waffle";
import {Link} from 'react-router-dom';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import {QueueRounded, TrafficRounded, UploadFileRounded} from "@mui/icons-material";
import {useSelector} from "react-redux";
import {selectPortfoliosWithSignals} from "../../../../store/slices/portfolios.slice";
/**
 *
 * @param portfolioCounts {Array[object]} - Array of objects containing portfolio counts and custom colors
 *                                          {id: "Models", label: "Models", value: 30, color: "#ff6347"}
 * @returns {JSX.Element}
 * @constructor
 */
const PortfolioWaffle = ({ portfolioCounts }) => {
    const total = portfolioCounts.reduce((acc, curr) => acc + curr.value, 0);

    return (
        <Row justify={'center'} wrap={false}>
            <Col flex={'0 0 70px'}>
                <div>
                    {portfolioCounts.filter(x=>x.value>0).map((portfolio) => (
                            <Tag style={{background: portfolio.color, color: "#fff", width: '60px', borderRadius: '2px'}}>
                                {portfolio.label}
                            </Tag>
                    ))}
                </div>
            </Col>
            <Col flex={'1'}>
                <div style={{ height: 75, width: '100%' }}>
                    <ResponsiveWaffle
                        data={portfolioCounts}
                        total={total} // Total count = sum of all values (e.g., 30 + 10 + 20)
                        rows={8} // Number of rows in the waffle grid
                        columns={36} // Number of columns in the waffle grid
                        fillDirection="right"
                        margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
                        borderWidth={1}
                        borderRadius={4}
                        colors={datum => datum?.color} // Use the color defined in the data

                        borderColor={{
                            from: 'color',
                            modifiers: [
                                ['brighter', 0.3],
                                ['opacity', 0.8]
                            ]
                        }} // Tile borders
                        animate={true}
                        motionStiffness={90}
                        motionDamping={11}
                    />
                </div>
            </Col>
        </Row>
    );
};


const PortfoliosToolbar = ({ onFilter }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    block: false,
    sleeve: true,
    model: true,
  });
  const portfolios = useSelector(selectPortfoliosWithSignals);


  // Debounced search to prevent excessive filtering while typing
  const debouncedSearch = useMemo(
    () => debounce((value) => onFilter({ searchTerm: value, filters }), 300),
    [filters, onFilter]
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Handle checkbox changes
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    console.log(name, checked)
    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters, [name]: checked };
      onFilter({ searchTerm, filters: updatedFilters });
      return updatedFilters;
    });
  };


  return (
    <Row gutter={[16, 16]} align={'middle'} justify={'space-between'}>
      <Col>
          <Row wrap={false} align={'middle'}>
              <Space direction={'vertical'}>
                  <Input
                        rootClassName={'primary mr-3'}
                        placeholder="Search portfolios by name"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        size={'large'}
                        style={{ width: '100%', minWidth: 300, maxWidth: 400}}
                    />
              </Space>
              <Col>
                  <Space wrap={true}>
                      <Checkbox className={'custom-checkbox mx-3'} name="model" checked={filters.model} onChange={handleCheckboxChange}>
                      Models
                    </Checkbox>
                    <Checkbox className={'custom-checkbox mx-3'} name="sleeve" checked={filters.sleeve} onChange={handleCheckboxChange}>
                      Sleeves
                    </Checkbox>
                    <Checkbox className={'custom-checkbox mx-3'} name="block" checked={filters.block} onChange={handleCheckboxChange}>
                      Blocks
                    </Checkbox>
                  </Space>
              </Col>
          </Row>
      </Col>
      <Col className={'mr-6'}>
          <Space>
              <Button variant={'outlined'} color={'primary'} startIcon={<QueueRounded/>}>
                  <Link to={'/builders/portfolio'}>
                      Add Portfolios
                  </Link>
              </Button>
              <Button variant={portfolios.length>0?'contained':'outlined'} disabled={portfolios.length===0} color={'primary'} startIcon={<TrafficRounded/>}>
                  <Link to={'/signals'}>Manage Signals</Link>
              </Button>
          </Space>
      </Col>
    </Row>
  );
};

export default PortfoliosToolbar;
